import {
  DEFAULT_STORAGE_PROFILE_NAME,
  initialConfig,
  writePanelConfig
} from '@mimir/web/desktop/config';
import { getUserDataPath, validateWritability, validateWritabilityIfStandaloneProfile } from '@mimir/web/desktop/utils';
import type { StorageProfileDialogRequest } from '@mimir/web/dialogs/storageProfileDialogRequest';
import { showToast, TOAST, DONT_AUTO_HIDE_TOAST } from '@mimir/web/eventBus';
import { hasSufficientStorageSettings } from '@mimir/web/model/storageProfile';
import { getStorageByIdFromState } from '@mimir/web/store/storeMisc';
import { getDefaultStorage } from '@mimir/web/util/localStorage';
import type { KeldaPathMapping, LocalPathInfo } from '@mimir/client-common/fileSync/premiereTypes';
import type { PanelConfig, StorageProfile } from '@mimir/web/desktop/storageSettings';
import type { IndexedFolder, KeldaConfigDto, LiveFolder } from '@mimir/common/dto/keldaConfigDto';
import { KELDA_INDEXED_FOLDERS_BUCKET, KELDA_LIVE_FOLDERS_BUCKET } from '@mimir/common/dto/keldaConfigPrimitives';
import { PREMIERE_STANDALONE_STORAGE_ID } from '@mimir/common/util/constants';
import { invariant } from '@mimir/common/util/invariant';
import cloneDeep from 'lodash/cloneDeep';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import Vue from 'vue';
import type { Module } from 'vuex';
import type { State } from './state';
import type { DesktopModuleState } from './desktop-module-state';
import { showStorageProfileDialog } from '../dialogs/storageProfile';
import type { AmeConfig } from '../desktop/ame/config';
import type { DvrConfig } from '../desktop/dvr/config';
import { defaultAmeConfig } from '../desktop/ame/config';
import { defaultDvrConfig } from '../desktop/dvr/config';
import { startPollingTransferProgress, stopPollingTransferProgress } from '../globals/transferProgress';
import { isInElectron, type AdobePanelContext, type ElectronContext } from '@mimir/common/context/applicationContext';
import type { UploadItem } from '@mimir/client-common/upload/UploadItem';
import { DefaultNotificationOps } from '../premiere/defaultNotificationOps';
import { ConsoleLogger } from '@mimir/common/util/logging';
import { DefaultOngoingDownloadOps } from '../premiere/defaultOngoingDownloadOps';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import { getGlobalStore } from '.';
import { DefaultKeldaPathOps } from '../premiere/defaultKeldaPathOps';
import { DefaultLocalStateOps } from '@mimir/client-common/fileSync/defaultLocalStateOps';
import { refreshPremiereClipInfos } from '../premiere/syncing';
import { disableDvrWorkflowIntegration } from '../dvr/mode';
import { launchWithConfig as launchAmeWithConfig } from '../desktop/ame/launch-step';
import { launchWithConfig as launchDvrWithConfig } from '../desktop/dvr/launch-step';

export const desktopModule: Module<DesktopModuleState, State> = {
  state: {
    enabled: false,
    localPathInfo: {},
    config: initialConfig,
    configInitialized: false,
    folderAvailabilityMap: {},
    keldaPathMappings: [],
    folderDragEnabled: true,
    desktopDragTimerId: null,
    dvrWorkflowIntegrationLaunched: false,
    dvrMode: false,
  },
  getters: {
    isProxyWorkflowEnabled: (state, getters, rootState, rootGetters) => getters.selectedStorageProfile.isProxyWorkflow,
    isProxyOnlyOptimizationsEnabled: (state, getters, rootState, rootGetters) => (getters.selectedStorageProfile.proxyOnlyOptimizations ?? false),
    isPartialHighresDownloadEnabled: (state, getters, rootState, rootGetters) => (getters.selectedStorageProfile.partialHighresDownload ?? false),
    disablePremiereConfigOverride: state => state.config.disableOverride ?? false,
    selectedStorageProfile: state => state.config.availableStorageProfiles[state.config.selectedStorageProfileName] || initialConfig.availableStorageProfiles[DEFAULT_STORAGE_PROFILE_NAME],
    premiereStorageProfiles: state => Object.values(state.config.availableStorageProfiles),
    selectedPresetPath: state => state.config.selectedPresetPath ?? '',
    downloadPath: (state, getters) => getters.selectedStorageProfile.downloadPath,
    dragUuidFileName: (state, getters) => getters.selectedStorageProfile.dragUuidFileName ?? false,
    sharedDownloadPath: (state, getters) => getters.selectedStorageProfile.sharedDownloadPath,
    localPathInfo: state => state.localPathInfo,
    hasStorageBeenSet: (state, getters) => {
      const storageId = getDefaultStorage();
      const downloadPath = getters.downloadPath;
      return !storageId || isEmpty(downloadPath);
    },
    folderAvailabilityMap: state => state.folderAvailabilityMap,
    keldaPathMappings: state => state.keldaPathMappings,
    folderDragEnabled: state => state.folderDragEnabled,
    configInitialized: state => state.configInitialized,
    desktopDragTimerId: state => state.desktopDragTimerId,
    ameConfig: state => state.config.ameConfig || defaultAmeConfig,
    dvrConfig: state => state.config.dvrConfig || defaultDvrConfig,
    dvrWorkflowIntegrationLaunched: state => state.dvrWorkflowIntegrationLaunched,
    dvrMode: state => state.dvrMode,
  },
  mutations: {
    setSelectedStorageProfile: (state, payload: StorageProfile) => {
      state.config.availableStorageProfiles[state.config.selectedStorageProfileName] = payload;
    },
    removeStorageProfile: (state, payload: StorageProfile) => {
      Vue.delete(state.config.availableStorageProfiles, payload.name);
    },
    addOrUpdateStorageProfile: (state, payload: StorageProfile) => {
      const profile = cloneDeep(payload);
      if (profile.isStandaloneMode) {
        profile.storageId = PREMIERE_STANDALONE_STORAGE_ID;
      } else if (profile.storageId === PREMIERE_STANDALONE_STORAGE_ID) {
        delete profile.storageId;
      }

      Vue.set(state.config.availableStorageProfiles, profile.name, profile);
    },
    setSelectedPresetPath: (state, presetPath: string) => {
      state.config.selectedPresetPath = presetPath;
    },
    setConfig: (state, payload: PanelConfig) => {
      state.config = payload;
      state.configInitialized = true;
    },
    initLocalMediaStatesAndPaths: (state, ids: string[]) => {
      const allPathInfos = {
        ...state.localPathInfo,
      };

      for (const id of ids) {
        if (!allPathInfos[id]) {
          allPathInfos[id] = {};
        }
      }

      Vue.set(state, 'localPathInfo', allPathInfos);
    },
    setLocalPathInfo: (state, payload: { id: string, localPathInfo: LocalPathInfo }) =>
      Vue.set(state.localPathInfo, payload.id, payload.localPathInfo),
    setLocalPathInfos: (state, payloads: { id: string, localPathInfo: LocalPathInfo }[]) => {
      const all = {
        ...state.localPathInfo,
      };

      payloads.map(pi => {
        all[pi.id] = pi.localPathInfo;
      });

      Vue.set(state, 'localPathInfo', all);
    },
    setSelectedStorageProfileName: (state, profileName: string) => {
      state.config.selectedStorageProfileName = profileName;
    },
    setLocalUploadDirectory: (state, payload: { storageKey: string, path: string | null }) => {
      let localSettings = state.config.localStorageSettingsByStorageKey;
      if (!localSettings) {
        localSettings = {};
        Vue.set(state.config, 'localStorageSettingsByStorageKey', localSettings);
      }

      invariant(localSettings != null, 'Defined at this point');
      let localStorageSettings = localSettings[payload.storageKey];
      if (!localStorageSettings) {
        localStorageSettings = {
          localFsPath: null
        };
        Vue.set(localSettings, payload.storageKey, localStorageSettings);
      }

      localStorageSettings.localFsPath = payload.path;
    },
    setFolderAvailability: (state, folderAvailability: { folderId: string, isAvailableLocally: boolean }) => {
      Vue.set(state.folderAvailabilityMap, folderAvailability.folderId, folderAvailability.isAvailableLocally);
    },
    setKeldaPathMappings: (state, pathMappings: KeldaPathMapping[]) => state.keldaPathMappings = pathMappings,
    folderDragEnabled: (state, enabled) => {
      state.folderDragEnabled = enabled;
    },
    desktopDragTimerId: (state, id) => {
      state.desktopDragTimerId = id;
    },
    setAmeConfig: (state, ameConfig: AmeConfig) => {
      Vue.set(state.config, 'ameConfig', ameConfig);
    },
    setDvrConfig: (state, dvrConfig: DvrConfig) => {
      Vue.set(state.config, 'dvrConfig', dvrConfig);
    },
    setDvrMode: (state, val: boolean) => {
      state.dvrMode = val;
    },
    setDvrWorkflowIntegrationLaunched: (state, val: boolean) => {
      state.dvrWorkflowIntegrationLaunched = val;
    },
  },
  actions: {
    setDvrMode: async ({ commit }, payload: { applicationContext: ElectronContext, val: boolean }) => {
      commit('setDvrMode', payload.val);
      commit('setDvrSidebarMenuVisible', payload.val, { root: true });
      payload.applicationContext.dvrMode = payload.val;
      if (payload.val) {
        await refreshDvrClipInfos(payload.applicationContext);

        // TODO: need to register the callback only once
        if (window.mimirElectron.onRefreshClipInfos) {
          window.mimirElectron.onRefreshClipInfos(() => refreshDvrClipInfos(payload.applicationContext));
        }
      }
    },
    setSelectedStorageProfile: async ({ commit }, payload: StorageProfile) => {
      commit('setSelectedStorageProfile', payload);
    },
    initLocalMediaStatesAndPaths: async ({ commit }, ids: string[]) => {
      commit('initLocalMediaStatesAndPaths', ids);
    },
    setLocalPathInfo: async ({ commit }, payload: { id: string, localPathInfo: LocalPathInfo }) => {
      commit('setLocalPathInfo', payload);
    },
    setLocalPathInfos: async ({ commit }, payloads: { id: string, localPathInfo: LocalPathInfo }[]) => {
      commit('setLocalPathInfos', payloads);
    },
    removeSelectedStorageProfile: async ({ state, getters, commit }, applicationContext: AdobePanelContext | ElectronContext) => {
      commit('removeStorageProfile', getters.selectedStorageProfile);
      commit('setSelectedStorageProfileName', DEFAULT_STORAGE_PROFILE_NAME);
      const services = {
        applicationContext,
        logger: new ConsoleLogger(),
      };
      await validateWritabilityIfStandaloneProfile(services, getters.selectedStorageProfile);
      await writePanelConfig(applicationContext, state.config);
      showToast('Storage profile deleted', TOAST.INFO);
    },
    addOrUpdateStorageProfile: async ({ state, getters, commit }, payload: { applicationContext: AdobePanelContext | ElectronContext, profile: StorageProfile }) => {
      const { applicationContext, profile } = payload;
      commit('addOrUpdateStorageProfile', profile);
      commit('setSelectedStorageProfileName', profile.name);
      const services = {
        applicationContext,
        logger: new ConsoleLogger(),
      };
      await validateWritabilityIfStandaloneProfile(services, getters.selectedStorageProfile);
      await writePanelConfig(applicationContext, state.config);
    },
    setSelectedPresetPath: async ({ state, commit }, payload: { applicationContext: AdobePanelContext | ElectronContext, presetPath: string }) => {
      const { applicationContext, presetPath } = payload;
      commit('setSelectedPresetPath', presetPath);
      await writePanelConfig(applicationContext, state.config);
    },
    setSelectedStorageProfileName: async ({ state, getters, commit }, payload: { applicationContext: AdobePanelContext | ElectronContext, profileName: string }) => {
      const { applicationContext, profileName } = payload;
      commit('setSelectedStorageProfileName', profileName);
      const services = {
        applicationContext,
        logger: new ConsoleLogger(),
      };
      await validateWritabilityIfStandaloneProfile(services, getters.selectedStorageProfile);
      await writePanelConfig(applicationContext, state.config);
    },
    saveConfig: async ({ state }, applicationContext: AdobePanelContext | ElectronContext) => {
      await writePanelConfig(applicationContext, state.config);
    },
    saveLocalUploadDirectory: async ({ state, commit }, payload: { applicationContext: AdobePanelContext | ElectronContext, storageKey: string, path: string | null }) => {
      commit('setLocalUploadDirectory', payload);
      invariant((state.config.localStorageSettingsByStorageKey ?? {})[payload.storageKey]?.localFsPath == payload.path, 'Expect mutation to work');
      await writePanelConfig(payload.applicationContext, state.config);
    },

    storageProfileChanged: async ({ dispatch, rootGetters }, payload: {
      downloadPath: string,
      applicationContext: AdobePanelContext | ElectronContext,
      id: string | undefined,
      ongoingUploads: UploadItem[],
      localStateOps: LocalStateOps,
    }) => {
      const { applicationContext, id, ongoingUploads, downloadPath, localStateOps } = payload;
      await dispatch('setDefaultStorage', getStorageByIdFromState(id), { root: true });
      const logger = new ConsoleLogger();
      stopPollingTransferProgress(logger);
      const ongoingDownloadOps = new DefaultOngoingDownloadOps();
      ongoingDownloadOps.clearOngoingDownloads();
      if (id) {
        const notificationOps = new DefaultNotificationOps(applicationContext);
        const services = {
          applicationContext,
          notificationOps,
          logger,
          ongoingDownloadOps,
          localStateOps,
        };
        const config = {
          downloadPath,
          userDataSystemPath: getUserDataPath(applicationContext),
          shouldUsePremierePreferences: rootGetters.usePremierePreferenceForScaleToFrame,
        };
        startPollingTransferProgress(services, config, ongoingUploads, id);
      }
    },
    loadStorageProfile: async ({ getters, dispatch }, payload: {
      applicationContext: AdobePanelContext | ElectronContext,
      toOpenPremiereProject?: boolean,
      ongoingUploads: UploadItem[],
      downloadPath: string,
      localStateOps: LocalStateOps,
    }) => {
      const { applicationContext, toOpenPremiereProject, ongoingUploads, downloadPath, localStateOps } = payload;
      const { selectedStorageProfile, configInitialized } = getters;
      if (!configInitialized) {
        showToast('Configuration has not been initialized properly. You may need to install an update.', TOAST.ERROR);
        return;
      }
      const storageValidity = hasSufficientStorageSettings(selectedStorageProfile);
      if (storageValidity.isSufficient) {
        if (!selectedStorageProfile.isStandaloneMode) {
          return selectedStorageProfile;
        }
        const services = {
          applicationContext,
          logger: new ConsoleLogger(),
        };
        const isWritable = await validateWritability(services, downloadPath, true);
        if (isWritable) {
          return selectedStorageProfile;
        }
      }

      let message = storageValidity.message;

      if (toOpenPremiereProject) {
        const premiereIntro = 'The Mimir extension needs a Media folder to store all media files it downloads.\n';
        message = message ? premiereIntro + message : premiereIntro + 'Please correct the current storage profile, or select a valid one.';
      }
      return new Promise((resolve, reject) => {
        const req: StorageProfileDialogRequest = {
          callback: (profile) => {
            if (profile) {
              storeDispatchStorageProfileChanged(
                applicationContext,
                downloadPath,
                profile.storageId,
                ongoingUploads,
                localStateOps,
              ).catch(err => console.error(err));
            }

            resolve(profile);
          },
          customSubHeaderMessage: message
        };
        showStorageProfileDialog(req);
      });
    },
    loadKeldaPathMappings: async (context) => {
      await context.dispatch('loadKeldaConfigs');
      const keldaConfigs = (context.getters.keldaConfigs ?? []) as KeldaConfigDto[];

      const mapFolderConfigToPaths = (keldaId: string, folder: LiveFolder | IndexedFolder, type: 'live' | 'indexed'): KeldaPathMapping[] => {
        if (!folder.clientReachablePaths) return [];
        const bucketName = type === 'live' ? KELDA_LIVE_FOLDERS_BUCKET : KELDA_INDEXED_FOLDERS_BUCKET;

        // Live items have the whole path from kelda root to bucket as their source path, whereas indexed items only contain the path
        // relative to the bucket
        const keldaRelativePathToFolder = type === 'live' ? `/kelda/kelda-root/${bucketName}/${folder.relativePath}` : folder.relativePath;

        return folder.clientReachablePaths.map(p => ({
          keldaId,
          type,
          userDefinedPathToFolder: p?.trim(),
          keldaRelativePathToFolder,
        }));
      };
      const pathMappings: KeldaPathMapping[] = [];
      for (const config of keldaConfigs) {
        const indexedFolders = config.indexedFolders ?? [];
        const liveFolders = config.liveFolders ?? [];
        pathMappings.push(
          ...flatMap(indexedFolders, f => mapFolderConfigToPaths(config.id, f, 'indexed')),
          ...flatMap(liveFolders, f => mapFolderConfigToPaths(config.id, f, 'live')),
        );

        if (config.clientReachablePaths) {
          pathMappings.push(...config.clientReachablePaths.map<KeldaPathMapping>(p => ({
            keldaId: config.id,
            type: 'primary-root',
            userDefinedPathToFolder: p.trim(),
            keldaRelativePathToFolder: '/kelda/kelda-root/primary-root',
          })));
        }
      }

      console.debug('Kelda path mappings:', JSON.stringify(pathMappings));

      context.commit('setKeldaPathMappings', pathMappings);
    },
    setAmeConfig: async ({ state, commit, rootGetters }, payload: { applicationContext: AdobePanelContext | ElectronContext, ameConfig: AmeConfig }) => {
      const { applicationContext, ameConfig } = payload;
      commit('setAmeConfig', ameConfig);
      await writePanelConfig(applicationContext, state.config);
      if (isInElectron(applicationContext)) {
        const services = {
          applicationContext,
          logger: new ConsoleLogger(),
          getDownloadPath: () => rootGetters.downloadPath,
        };
        launchAmeWithConfig(services, ameConfig);
      }
    },
    handleBrokenDvrConnection: async (_, applicationContext: ElectronContext) => {
      // DVR is not running, or plugin not initialized. Show a persistent toast. Error details are not needed in the toast.
      showToast('Please check that DaVinci Resolve is running and click Workspace > Workflow Integrations > Mimir', TOAST.ERROR, DONT_AUTO_HIDE_TOAST);

      // Turn off DVR features and stop polling open projects
      disableDvrWorkflowIntegration(applicationContext);
    },
    setDvrConfig: async ({ state, commit, getters, rootGetters }, payload: { applicationContext: AdobePanelContext | ElectronContext, dvrConfig: DvrConfig }) => {
      const { applicationContext, dvrConfig } = payload;
      commit('setDvrConfig', dvrConfig);
      await writePanelConfig(applicationContext, state.config);
      if (isInElectron(applicationContext)) {
        const services = {
          applicationContext,
          logger: new ConsoleLogger(),
          getDownloadPath: () => rootGetters.downloadPath,
        };
        launchDvrWithConfig(services, dvrConfig);
      }
    },
  },
};

export async function refreshDvrClipInfos(applicationContext: ElectronContext) {
  const logger = new ConsoleLogger();
  const ongoingDownloadOps = new DefaultOngoingDownloadOps();
  const store = getGlobalStore();
  const keldaPathOps = new DefaultKeldaPathOps(applicationContext, logger, store.getters.keldaPathMappings);
  const notificationOps = new DefaultNotificationOps(applicationContext);
  const services = {
    applicationContext: applicationContext,
    notificationOps,
    logger,
    ongoingDownloadOps,
    keldaPathOps,
    localStateOps: new DefaultLocalStateOps(applicationContext, logger, ongoingDownloadOps, keldaPathOps, store.getters.downloadPath),
  };

  // TODO: refactor functions like these to be non-Premiere specific.
  return refreshPremiereClipInfos(services, store.getters.ongoingUploads);
}

export async function storeDispatchStorageProfileChanged(
  applicationContext: AdobePanelContext | ElectronContext,
  downloadPath: string,
  id: string | undefined,
  ongoingUploads: UploadItem[],
  localStateOps: LocalStateOps,
): Promise<void> {
  await getGlobalStore().dispatch('storageProfileChanged', {
    applicationContext,
    downloadPath,
    id,
    ongoingUploads,
    localStateOps,
  });
}
